import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { ENVIRONMENT_TOKEN, EnvironmentToken, IConfigGlobalePortaleSaaS } from '@ebw-monorepo/utils';
import { jsPDF } from 'jspdf';
import { LoaderService } from 'libs/ng-components/loading/src/lib/services/loader.service';
import BaseLayer from 'ol/layer/Base';
import { getPointResolution } from 'ol/proj';
import { toSize } from 'ol/size';
import { Observable, debounceTime, map, take } from 'rxjs';
import { CONFIG_SERVICE_ADAPTER, IConfigService } from '../../interfaces/config-service.interface';
import { MapService } from './../../services/map.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ebw-print-map',
  templateUrl: './print-map.component.html',
  styleUrls: ['./print-map.component.scss']
})
export class PrintMapComponent implements OnInit, OnDestroy {
  NONE_LAYER_KEY = 'none';
  /**
   * List of page sizes.
   */
  @ViewChild('upload') upload: ElementRef;
  @ViewChild('dpi') dpi: ElementRef;

  public settingsForm: FormGroup;
  public previewBase64;
  public pdfDoc;
  public pdfUrl;

  public dimOptions: any[];
  public dpiOptions = [
    { label: '72', value: 72 },
    { label: '150', value: 150 },
    { label: '200', value: 200 },
    { label: '300', value: 300 }
  ];

  public scaleOptions = [
    { label: 'Utente', value: null },
    { label: '1:100000', value: 100 },
    { label: '1:50000', value: 50 },
    { label: '1:25000', value: 25 },
    { label: '1:10000', value: 10 },
    { label: '1:5000', value: 5 },
    { label: '1:2500', value: 2.5 },
    { label: '1:2000', value: 2 },
    { label: '1:1500', value: 1.5 },
    { label: '1:1000', value: 1 },
    { label: '1:500', value: 0.5 },
    { label: '1:250', value: 0.25 }
  ];

  public dims = {
    a3: [420, 297],
    a4: [297, 210],
    a5: [210, 148]
  };

  private _defaultformValue = {
    dim: this.dims.a3,
    title: 'Esempio Intestazione',
    dpi: 72,
    scale: null,
    orientation: 'landscape'
  };

  private _mapCanvas: HTMLCanvasElement;
  private _pdfBlobUrl: URL;
  private _userScale;
  private _globalXMarginSize = 5;
  private _globalYMarginSize = 10;
  private _currentConfig: IConfigGlobalePortaleSaaS;
  private _intialResolutionConstraint: boolean;

  private _previousZoomLevels: number[];
  private _previousLayerZoomLevels: Map<string, number[]> = new Map<string, number[]>();

  currentLayer: string;

  constructor(
    private _detectChangeRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private mapSrv: MapService,
    public dialogRef: MatDialogRef<PrintMapComponent>,
    public translateService: I18NService,
    private httpClient: HttpClient,
    private loaderService: LoaderService,
    private loginService: LoginService,
    // private configService: ConfigService
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(ENVIRONMENT_TOKEN) private environment: EnvironmentToken,
    @Inject(CONFIG_SERVICE_ADAPTER) private _configService: IConfigService
  ) {}

  ngOnInit(): void {
    // get the map of active layers
    const activeLayers = this.mapSrv.activeLayers;

    // find all the currently visible active layers
    activeLayers.forEach((layer: BaseLayer) => {
      if (layer.getVisible()) {
        // store their current zoom limits
        this._previousLayerZoomLevels.set(layer.getProperties().id, [layer.getMinZoom(), layer.getMaxZoom()]);

        // remove all the zoom limits of the layer
        layer.setMinZoom(0);
        layer.setMaxZoom(Infinity);
      }
    });

    // remove the zoom limits on the View while storing its previous values
    const view = this.mapSrv.openLayersMap.getView();
    this._previousZoomLevels = [view.getMinZoom(), view.getMaxZoom()];
    view.setMinZoom(0);
    view.setMaxZoom(Infinity);

    this._currentConfig = this._configService.getConfigGlobale();
    this._intialResolutionConstraint = this.mapSrv.openLayersMap.getView().getProperties().constrainResolution;

    this.dimOptions = [
      { label: 'A3', value: this.dims.a3 },
      { label: 'A4', value: this.dims.a4 },
      { label: 'A5', value: this.dims.a5 }
    ];

    if (this._currentConfig) {
      this._defaultformValue.title = this._currentConfig.styles?.printTool?.defaultTitle || 'Esempio intestazione';
      this._defaultformValue.dpi = this._currentConfig.styles?.printTool?.defaultDpi || 72;
      this._defaultformValue.dim =
        this.dims[this._currentConfig.styles?.printTool?.defaultPageSize.toLowerCase()] || this.dims.a3;

      let basePathLogo = './assets/custom';
      const tenantName = this.loginService.getTenantName();
      const isSaas = this.environment.deployMode === 'saas';
      basePathLogo += isSaas ? `/${tenantName}` : '';
      const nomeLogoImg = 'logo_print-tool.png';
      const logoUrl = `${basePathLogo}/${nomeLogoImg}`;
      this.getImageAsFile(logoUrl)
        .pipe(take(1))
        .subscribe((file) => {
          this.settingsForm.patchValue({
            image: file
          });
        });
    } else {
      this._drawCanvas(this._defaultformValue);
    }

    this._initForm();

    this.currentLayer = this.mapSrv.activeBaseLayer.get(MapService.LABEL_LAYER_KEY);
    if (this.currentLayer !== 'Nessuno') {
      setTimeout(() => {
        for (const [_, layer] of this.mapSrv.activeLayers.entries()) {
          if (layer.get(MapService.LABEL_LAYER_KEY) == 'Open Street Map') {
            this.mapSrv.changeWorldLayer(layer.get(MapService.ID_LAYER_KEY));
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this._pdfBlobUrl) {
      URL.revokeObjectURL(this._pdfBlobUrl.toString());
    }

    // set back the previous values of the zoom limits for the view and the individual layers
    const view = this.mapSrv.openLayersMap.getView();
    view.setMinZoom(this._previousZoomLevels[0]);
    view.setMaxZoom(this._previousZoomLevels[1]);

    for (const [id, zoomLevels] of this._previousLayerZoomLevels.entries()) {
      this.mapSrv.activeLayers.get(id).setMinZoom(zoomLevels[0]);
      this.mapSrv.activeLayers.get(id).setMaxZoom(zoomLevels[1]);
    }
  }

  public onFileSelect(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.settingsForm.get('image').setValue(file);
      this.settingsForm.get('image_').setValue(file.name);
    }
  }

  public downloadPDF() {
    if (this._mapCanvas) {
      this._generatePDF(false);
    }
  }

  private async _generatePDF(preview) {
    const userSettings = this.settingsForm.getRawValue();
    this.pdfDoc = new jsPDF(userSettings.orientation, 'mm', userSettings.dim);

    const pdfWidth = this.pdfDoc.internal.pageSize.getWidth();
    const pdfHeight = this.pdfDoc.internal.pageSize.getHeight();
    let yOffeset = this._globalYMarginSize;

    this.pdfDoc.setFontSize(12);
    this.pdfDoc.text(userSettings.title || '', this._globalXMarginSize, yOffeset, {
      maxWidth: pdfWidth - this._globalXMarginSize * 2 - 30
    });
    const textDimensionTitle = this.pdfDoc.getTextDimensions(userSettings.title || '');
    yOffeset += textDimensionTitle.h;

    this.pdfDoc.setFontSize(9);
    this.pdfDoc.setTextColor(147, 147, 147);

    let scaleToDisplay;

    if (this._userScale) {
      scaleToDisplay = `1:${this._userScale * 1000}`;
    } else {
      const textOpt = this.scaleOptions.find((_) => _.value === userSettings.scale);

      scaleToDisplay = textOpt.label;
    }

    this.pdfDoc.text(scaleToDisplay || '', this._globalXMarginSize, yOffeset, {});

    const textDimensionScale = this.pdfDoc.getTextDimensions(scaleToDisplay || '');

    yOffeset += textDimensionScale.h;

    if (userSettings.image) {
      const logoDimensions: any = await this._getFileImageDimensions(userSettings.image, 30);
      const logoX = pdfWidth - logoDimensions.width - this._globalXMarginSize;
      const logoY = this._globalYMarginSize - logoDimensions.height / 2;

      this.pdfDoc.addImage(
        URL.createObjectURL(userSettings.image),
        'JPEG',
        logoX,
        logoY,
        logoDimensions.width,
        logoDimensions.height,
        '',
        'FAST'
      );
    }

    const imageMapData = this._mapCanvas.toDataURL('image/jpeg', preview ? 0.5 : 1);

    // const imgProps = this.pdfDoc.getImageProperties(imageMapData);

    //(imgProps.height * pdfWidth) / imgProps.width;

    this.pdfDoc.addImage(
      imageMapData,
      'JPEG',
      this._globalXMarginSize,
      17,
      pdfWidth - this._globalXMarginSize * 2,
      pdfHeight - (17 + this._globalXMarginSize)
    );

    this.pdfDoc.rect(
      this._globalXMarginSize,
      17,
      pdfWidth - this._globalXMarginSize * 2,
      pdfHeight - (17 + this._globalXMarginSize),
      'S'
    );

    if (preview) {
      if (this._pdfBlobUrl) {
        URL.revokeObjectURL(this._pdfBlobUrl.toString());
      }
      this._pdfBlobUrl = this.pdfDoc.output('bloburl');
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this._pdfBlobUrl}#toolbar=0&statusbar=0`);
    } else {
      this.pdfDoc.save(`${new Date().toISOString()}-map.pdf`);
    }

    this._detectChangeRef.detectChanges();
  }

  private _drawCanvas(settings: any, preview = true) {
    try {
      this.loaderService.isLoading.next(true);
      const isLand = settings.orientation === 'landscape';
      const resolution = settings.dpi;
      const dim = settings.dim;

      let scale = settings.scale;

      if (!settings.scale) {
        // 90.71428571 = DPI OGC maps: 0.28 mm/px => 1 / 0.28mm/px = 3.571 px/mm = 35.71 px/cm * 2.54 cm/in = 90.714 px/in
        // 50.8 = constant calculated from the scale
        scale = Math.ceil((this.mapSrv.openLayersMap.getView().getResolution() * 90.71428571 * 50.8) / 1000);
        this._userScale = scale;
      } else {
        this._userScale = null;
      }

      // setto le impostazione della canvas togliendo i margini utilizzati nel pdf
      // 17mm spazio per l'intestazione del pdf
      const mapWidhtMM = dim[isLand ? 0 : 1] - this._globalXMarginSize * 2;
      const mapHeightMM = dim[isLand ? 1 : 0] - (this._globalXMarginSize + 17);
      const width = Math.round((mapWidhtMM * resolution) / 25.4);
      const height = Math.round((mapHeightMM * resolution) / 25.4);
      const viewResolution = this.mapSrv.openLayersMap.getView().getResolution();
      const scaleResolution =
        scale /
        getPointResolution(
          this.mapSrv.openLayersMap.getView().getProjection(),
          resolution / 25.4,
          this.mapSrv.openLayersMap.getView().getCenter()
        );

      const size: any = [...this.mapSrv.openLayersMap.getSize()];
      const extent = this.mapSrv.openLayersMap.getView().calculateExtent(size);

      this.mapSrv.openLayersMap.once('rendercomplete', (event) => {
        this._mapCanvas = this._cloneCanvas(document.getElementById('ol-map').querySelector('canvas'));
        this._generatePDF(preview);
        // // Reset original map size
        this.mapSrv.openLayersMap.getView().setConstrainResolution(this._intialResolutionConstraint);
        this.mapSrv.openLayersMap.setSize(toSize([size[0], size[1]]));
        this.mapSrv.openLayersMap.getView().fit(extent, { size: size });
        this.mapSrv.openLayersMap.getView().setResolution(viewResolution);
        this.mapSrv.changeWorldLayer(this.currentLayer);
        this.loaderService.isLoading.next(false);

        this._detectChangeRef.detectChanges();
      });

      setTimeout(() => {
        this.mapSrv.openLayersMap.getView().setConstrainResolution(false);
        this.mapSrv.openLayersMap.getView().setResolution(scaleResolution);
        this.mapSrv.openLayersMap.setSize([width, height]);
      }, 0);
    } catch (error) {
      console.error(error);
      this.loaderService.isLoading.next(false);
    }
  }

  private _initForm() {
    this.settingsForm = new FormBuilder().group({
      title: new FormControl(this._defaultformValue.title),
      dim: new FormControl(this._defaultformValue.dim),
      dpi: new FormControl(this._defaultformValue.dpi),
      scale: new FormControl(this._defaultformValue.scale),
      image: new FormControl(null),
      image_: new FormControl(null),
      orientation: new FormControl(this._defaultformValue.orientation)
    });

    this.settingsForm.valueChanges.pipe(debounceTime(800)).subscribe((val) => {
      if (this.currentLayer !== 'None') {
        setTimeout(() => {
          this.mapSrv.changeWorldLayer('Open Street Map');
        });
      }
      this._drawCanvas(val);
    });
  }

  private _getFileImageDimensions(file, maxWidth) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const image: any = new Image();

        image.onload = (e: any) => {
          // Calcola il fattore di scala per l'immagine
          const scaleFactor = maxWidth / e.target.width;

          // Calcola la nuova larghezza e altezza dell'immagine
          const newWidth = e.target.width * scaleFactor;
          const newHeight = e.target.height * scaleFactor;

          resolve({ width: newWidth, height: newHeight });
        };

        image.src = event.target.result;
      };

      reader.readAsDataURL(file);
    });
  }

  private getImageAsFile(imagePath: string): Observable<File> {
    return this.httpClient.get(imagePath, { responseType: 'blob' }).pipe(map((blob) => new File([blob], 'image.jpg')));
  }

  private _cloneCanvas(oldCanvas) {
    //create a new canvas
    const newCanvas = document.createElement('canvas');
    const context = newCanvas.getContext('2d');

    //set dimensions
    newCanvas.width = oldCanvas.width;
    newCanvas.height = oldCanvas.height;

    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, oldCanvas.width, oldCanvas.height);

    //draw the original canvas onto the destination canvas
    context.drawImage(oldCanvas, 0, 0);

    //return the new canvas
    return newCanvas;
  }
}
